import {EmptyStatus} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useEffect, useMemo, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  chatAgentApi,
  useGetParticipationQuery,
  useSubscribeToChatConversationMutation,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {BusinessCaseChatResponseBody, GetBusinessCaseApiResponse} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useBcToOnlineOffer} from '@dms/shared';

import {AgentMessage} from './AgentMessage';
import {CustomerMessage} from './CustomerMessage';

interface TimelineProps {
  conversationIds: string[];
  activeConversationId: string;
  businessCaseId?: string;
  refetchBusinessCase: () => Promise<{
    data?: GetBusinessCaseApiResponse;
  }>;
  onMediaClick?: () => void;
}

export function Timeline(props: TimelineProps) {
  const dispatch = useDispatch();
  const {data: userInfo} = useGetCurrentUserInfoQuery();

  const currentUserId = userInfo?.id || '';

  const {isOnlineOfferCreated, actions, canSendOffer} = useBcToOnlineOffer(
    props.businessCaseId ?? ''
  );

  const [subscribeToChatConversation] = useSubscribeToChatConversationMutation();
  const [markAgentEventsAsRead] = chatAgentApi.useMarkAgentEventsAsReadMutation();

  const conversationIdsWithoutActiveConversation = props.conversationIds.filter(
    (conversationId) => conversationId !== props.activeConversationId
  );

  const previousEventsQuery = chatAgentApi.useGetManyAgentEventsQuery(
    {
      conversationIds: conversationIdsWithoutActiveConversation,
    },
    {skip: isNilOrEmpty(conversationIdsWithoutActiveConversation)}
  );

  const activeEventsQuery = chatAgentApi.useGetAgentEventsQuery(
    {
      conversationId: props.activeConversationId,
    },
    {skip: isNilOrEmpty(props.activeConversationId)}
  );

  const conversationQuery = chatAgentApi.useGetAgentConversationQuery(
    {
      conversationId: props.activeConversationId,
    },
    {skip: isNilOrEmpty(props.activeConversationId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: props.businessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const currentBcOwnerId = businessCaseParticipation?.owner?.userId;

  const users = conversationQuery.data?.users;

  const handleChatInvalidation = () => {
    setTimeout(async () => {
      const updatedBusinessCase = await props.refetchBusinessCase();
      const newActiveConversation = updatedBusinessCase.data?.chatConversations?.find(
        (conv: BusinessCaseChatResponseBody) => conv.isActive
      );

      if (newActiveConversation?.conversationId) {
        try {
          await subscribeToChatConversation({
            conversationId: newActiveConversation.conversationId,
          }).unwrap();
        } catch (error) {
          console.error('Failed to subscribe to chat conversation:', error);
        }
      }

      await props.refetchBusinessCase();

      dispatch(chatAgentApi.util.invalidateTags(['ConversationEvents', 'Conversation']));
    }, 5000);
  };

  const previousEvents = useMemo(
    () => previousEventsQuery.data?.flatMap((conversation) => conversation.events) ?? [],
    [previousEventsQuery.data]
  );

  const activeEvents = useMemo(
    () => (props.activeConversationId ? (activeEventsQuery.data ?? []) : []),
    [activeEventsQuery.data, props.activeConversationId]
  );

  const events = useMemo(
    () => [...previousEvents, ...activeEvents],
    [previousEvents, activeEvents]
  );

  const lastEvent = events[events.length - 1];

  const isAgentPartOfConversation = !!users?.find((user) => user.externalUserId === currentUserId);

  const isReadByAgent = !!lastEvent?.readBy.find(
    (readBy) => readBy.user.externalUserId === currentUserId
  );

  const shouldMarkAsRead = isAgentPartOfConversation && !isReadByAgent;

  const lastMessageRef = useRef<HTMLDivElement>(null);
  const hasMessages = events.length > 0;

  const scrollToLastMessage = () => {
    requestAnimationFrame(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  };

  useEffect(() => {
    if (hasMessages) {
      scrollToLastMessage();
    }
  }, [events, hasMessages]);

  useEffect(() => {
    if (hasMessages && props.activeConversationId) {
      scrollToLastMessage();
    }
  }, [props.activeConversationId, hasMessages]);

  useEffect(() => {
    if (shouldMarkAsRead) {
      markAgentEventsAsRead({conversationId: props.activeConversationId});
    }
  }, [props.activeConversationId, markAgentEventsAsRead, currentUserId, shouldMarkAsRead]);

  return (
    <Box overflow="auto" height="100%" padding={4}>
      <Show when={!isOnlineOfferCreated && !hasMessages}>
        <VStack justify="center" height="100%">
          <EmptyStatus
            headline={i18n.t('general.labels.noMessages')}
            subheadline={i18n.t('entity.chat.labels.offerNotSharedWithCustomer')}
            variant="message"
            action={{
              title: i18n.t('entity.chat.labels.shareOffer'),
              variant: 'secondary',
              isDisabled: !canSendOffer,
              onClick: () =>
                actions.sendOnlineOfferToCustomer({
                  shouldCreateOffer: true,
                  onSuccess: handleChatInvalidation,
                }),
            }}
          />
        </VStack>
      </Show>

      <Show when={isOnlineOfferCreated && !hasMessages}>
        <VStack justify="center" height="100%">
          <EmptyStatus headline={i18n.t('general.labels.noMessages')} variant="message" />
        </VStack>
      </Show>

      <Show when={hasMessages}>
        <VStack spacing={4}>
          {events.map((item, index) => {
            const isReadByBcOwner = !!item.readBy.find(
              (readBy) => readBy.user.externalUserId === currentBcOwnerId
            )?.readAt;
            const isReadByPublicUser = !!item.readBy.find((readBy) => readBy.user.type === 'PUBLIC')
              ?.readAt;

            const isLastMessage = index === events.length - 1;
            const messageRef = isLastMessage ? lastMessageRef : undefined;

            const messageStatus = (() => {
              if (item.type === 'PLAIN') {
                if (item.sender.type === 'DMS') {
                  return isReadByPublicUser ? 'read' : 'received';
                }
                return isReadByBcOwner ? 'read' : 'received';
              } else if (item.type === 'WHATSAPP') {
                if (item.readAt) {
                  return 'read';
                } else if (item.deliveredAt) {
                  return 'received';
                } else {
                  return 'sent';
                }
              }
              return 'sent';
            })();

            return item.sender.type === 'DMS' ? (
              <div ref={messageRef} key={item.id}>
                <AgentMessage
                  event={item}
                  messageStatus={messageStatus}
                  onMediaClick={props.onMediaClick}
                  showReadStatus
                />
              </div>
            ) : (
              <div ref={messageRef} key={item.id}>
                <CustomerMessage
                  event={item}
                  messageStatus={messageStatus}
                  onMediaClick={props.onMediaClick}
                />
              </div>
            );
          })}
        </VStack>
      </Show>
    </Box>
  );
}
